import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PageResponse } from './pagination.types';

export interface ArquivoComprovatorio {
  url: string;
  dataImportacao: string;
}

export interface DataChangeRequest {
  id: number;
  usuarioId: number;
  username: string;
  campoAlterado: string;
  valorAntigo: string;
  valorNovo: string;
  caminhoDocumento?: string; // Mantido para compatibilidade
  arquivoComprovatorio?: ArquivoComprovatorio;
  justificativaRejeicao?: string;
  status: 'PENDENTE' | 'APROVADO' | 'REJEITADO';
  dataSolicitacao: string;
  dataAnalise?: string;
  avaliadorId?: number;
  avaliadorUsername?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDataChangeRequest {
  campoAlterado: string;
  valorNovo: string;
  caminhoDocumento: string;
}

export interface EvaluateDataChangeRequest {
  status: 'APROVADO' | 'REJEITADO' | 'PENDENTE';
  justificativaRejeicao?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataChangeRequestService {
  private apiUrl = `${environment.apiUrl}/solicitacoes-alteracao`;

  constructor(private http: HttpClient) {}

  /**
   * Buscar solicitação por ID
   */
  getById(id: number): Observable<DataChangeRequest> {
    return this.http.get<DataChangeRequest>(`${this.apiUrl}/${id}`);
  }

  /**
   * Lista solicitações pendentes de avaliação
   * Requer permissão DADOS_ALTER_VISUALIZAR no edital especificado
   */
  getPendingRequests(params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
  }): Observable<PageResponse<DataChangeRequest>> {
    let httpParams = new HttpParams()

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    return this.http.get<PageResponse<DataChangeRequest>>(`${this.apiUrl}/pendentes`, { params: httpParams });
  }

  /**
   * Lista todas as solicitações de alteração do usuário autenticado
   */
  getMyRequests(): Observable<DataChangeRequest[]> {
    return this.http.get<DataChangeRequest[]>(`${this.apiUrl}/minhas`);
  }

  /**
   * Lista todas as solicitações de alteração do candidato pendentes
   */
  getMyPendingRequests(): Observable<DataChangeRequest[]> {
    return this.http.get<DataChangeRequest[]>(`${this.apiUrl}/minhas/pendentes`);
  }

  /**
   * Cria uma nova solicitação de alteração de dados sensíveis
   * Bloqueia novas solicitações se houver uma pendente
   */
  createRequest(request: CreateDataChangeRequest): Observable<DataChangeRequest> {
    return this.http.post<DataChangeRequest>(this.apiUrl, request);
  }

  /**
   * Avaliar solicitação
   * Requer permissão global DADOS_ALTER_DEFERIR (sem vínculo com edital)
   */
  evaluateRequest(id: number, evaluation: EvaluateDataChangeRequest): Observable<DataChangeRequest> {
    return this.http.post<DataChangeRequest>(`${this.apiUrl}/${id}/avaliar`, evaluation);
  }

  /**
   * Upload de arquivo comprovatório para solicitação de alteração
   * Retorna o caminho do documento que deve ser usado ao criar a solicitação
   */
  uploadDocument(file: File): Observable<{ caminhoDocumento: string; nomeArquivo: string; tamanhoBytes: number; tipoConteudo: string; mensagem: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ caminhoDocumento: string; nomeArquivo: string; tamanhoBytes: number; tipoConteudo: string; mensagem: string }>(
      `${this.apiUrl}/upload`, 
      formData
    );
  }

  /**
   * Visualizar arquivo comprovatório de uma solicitação
   * Retorna o arquivo como blob para visualização/download
   */
  getDocument(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/documento`, { responseType: 'blob' });
  }
}
